import React, { ReactNode, useState } from 'react'
import { Layout, Spin, Button, theme, Dropdown, Avatar, Badge, Space, Modal, Form, Select, Input, App, Descriptions, Typography, Popover } from 'antd'
import {
  UserOutlined,
  DashboardOutlined,
  SettingOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  BellOutlined,
  GlobalOutlined,
  MessageOutlined,
  InfoCircleOutlined,
  MailOutlined,
  EnvironmentOutlined,
  BankOutlined
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../stores/authStore'
import { useNotificationStore } from '../../stores/notificationStore'
import { useNotificationWebSocket } from '@/shared/hooks/useNotificationWebSocket'
import { getRoleDisplayName } from '@/shared/utils/roleUtils'
import NotificationPanel from '../layout/NotificationPanel'
import { api } from '@/shared/api/client'
import Header from '../layout/Header'
import Sidebar from '../layout/Sidebar'

const { Content, Sider } = Layout

interface AuthLayoutProps {
  children: ReactNode
  showSidebar?: boolean
  showHeader?: boolean
  fullScreen?: boolean
}

const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  showSidebar = true,
  showHeader = true,
  fullScreen = false
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation(['common', 'modules'])
  const { user, company, logout } = useAuthStore()
  const unreadCount = useNotificationStore((s) => s.unreadCount)
  useNotificationWebSocket({ companyId: company?.id })
  const [collapsed, setCollapsed] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [aboutOpen, setAboutOpen] = useState(false)
  const [feedbackLoading, setFeedbackLoading] = useState(false)
  const [feedbackForm] = Form.useForm()
  const { message: antMessage } = App.useApp()

  const {
    token: { colorBgContainer, borderRadiusLG }
  } = theme.useToken()

  // 处理退出登录
  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('退出登录失败:', error)
    }
  }


  // 处理用户菜单点击
  const handleUserMenuClick = (e: any) => {
    if (e.key === 'logout') {
      handleLogout()
    } else if (e.key === 'profile') {
      navigate('/profile')
    } else if (e.key === 'settings') {
      navigate('/settings')
    }
  }

  // 用户菜单项
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('user.profile', { ns: 'common' }) || '个人资料',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('user.settings', { ns: 'common' }) || '账户设置',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('user.logout', { ns: 'common' }) || '退出登录',
    },
  ]

  const handleFeedbackSubmit = async () => {
    const values = await feedbackForm.validateFields()
    setFeedbackLoading(true)
    try {
      await api.post('/suggestions', values)
      antMessage.success(t('help.feedbackModal.submitSuccess', { ns: 'common' }))
      feedbackForm.resetFields()
      setFeedbackOpen(false)
    } finally {
      setFeedbackLoading(false)
    }
  }

  const handleHelpMenuClick = (e: any) => {
    if (e.key === 'feedback') setFeedbackOpen(true)
    else if (e.key === 'about') setAboutOpen(true)
  }

  const helpMenuItems = [
    {
      key: 'feedback',
      icon: <MessageOutlined />,
      label: t('help.feedback', { ns: 'common' }),
    },
    {
      key: 'about',
      icon: <InfoCircleOutlined />,
      label: t('help.about', { ns: 'common' }),
    },
  ]

  // 全屏模式
  if (fullScreen) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        {showHeader && <Header />}
        <Content>{children}</Content>
      </Layout>
    )
  }

  // 默认布局（带侧边栏）
  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 侧边栏 */}
      {showSidebar && (
        <Sidebar
          collapsed={collapsed}
          onCollapse={setCollapsed}
          width={68}
          collapsedWidth={48}
          style={{
            background: colorBgContainer,
            boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 1000
          }}
        />
      )}

      {/* 主内容区域 */}
      <Layout
        style={{
          marginLeft: showSidebar ? (collapsed ? 48 : 68) : 0,
          transition: 'all 0.2s'
        }}
      >
        {/* 头部 */}
        {showHeader && (
          <Layout.Header
            style={{
              padding: '0 24px',
              background: colorBgContainer,
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'sticky',
              top: 0,
              zIndex: 999
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {/* 页面标题 */}
              <div style={{ fontSize: '16px', fontWeight: 500 }}>
                {getPageTitle(location.pathname, t)}
              </div>
            </div>

            {/* 用户信息 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {/* 公司信息 */}
              {company && (
                <div style={{ textAlign: 'right', lineHeight: '1.2' }}>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: '#1890ff' }}>{company.name}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {getRoleDisplayName(user?.role || '', t)} · {user?.username || t('role.user')}
                  </div>
                </div>
              )}

              {/* 功能按钮组 */}
              <Space size="middle">
                {/* 帮助菜单 */}
                <Dropdown menu={{ items: helpMenuItems, onClick: handleHelpMenuClick }} placement="bottomRight">
                  <Button type="text" icon={<QuestionCircleOutlined />} />
                </Dropdown>

                {/* 通知 */}
                <Popover content={<NotificationPanel />} trigger="click" placement="bottomRight" arrow={false}>
                  <Badge count={unreadCount} size="small">
                    <Button type="text" icon={<BellOutlined />} />
                  </Badge>
                </Popover>

                {/* 用户菜单 */}
                <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }} placement="bottomRight">
                  <Space style={{ cursor: 'pointer' }}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: '#1890ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                    >
                      {user?.username?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    {!collapsed && user && (
                      <span style={{ fontSize: '14px' }}>{user.username}</span>
                    )}
                  </Space>
                </Dropdown>
              </Space>
            </div>
          </Layout.Header>
        )}

        {/* 内容区域 */}
        <Content
          style={{
            margin: location.pathname === '/chatbot' ? 0 : '16px',
            padding: location.pathname === '/chatbot' ? 0 : 20,
            minHeight: 280,
            background: location.pathname === '/chatbot' ? 'transparent' : colorBgContainer,
            borderRadius: location.pathname === '/chatbot' ? 0 : borderRadiusLG,
            overflow: 'auto',
            height: location.pathname === '/chatbot' ? 'calc(100vh - 64px)' : undefined // 固定高度给聊天窗口
          }}
        >
          {children}
        </Content>

        {/* 反馈建议弹窗 */}
        <Modal
          title={t('help.feedbackModal.title', { ns: 'common' })}
          open={feedbackOpen}
          onOk={handleFeedbackSubmit}
          onCancel={() => { setFeedbackOpen(false); feedbackForm.resetFields() }}
          confirmLoading={feedbackLoading}
          okText={t('submit')}
          cancelText={t('cancel')}
          destroyOnHidden
        >
          <Form form={feedbackForm} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item
              name="type"
              label={t('help.feedbackModal.type', { ns: 'common' })}
              rules={[{ required: true }]}
            >
              <Select placeholder={t('help.feedbackModal.typePlaceholder', { ns: 'common' })}>
                <Select.Option value="feature">{t('help.feedbackModal.type.feature', { ns: 'common' })}</Select.Option>
                <Select.Option value="bug">{t('help.feedbackModal.type.bug', { ns: 'common' })}</Select.Option>
                <Select.Option value="ui">{t('help.feedbackModal.type.ui', { ns: 'common' })}</Select.Option>
                <Select.Option value="other">{t('help.feedbackModal.type.other', { ns: 'common' })}</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="content"
              label={t('help.feedbackModal.content', { ns: 'common' })}
              rules={[{ required: true }]}
            >
              <Input.TextArea
                rows={4}
                placeholder={t('help.feedbackModal.contentPlaceholder', { ns: 'common' })}
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* 关于系统弹窗 */}
        <Modal
          title={t('help.aboutModal.title', { ns: 'common' })}
          open={aboutOpen}
          onCancel={() => setAboutOpen(false)}
          footer={null}
          width={480}
        >
          <div style={{ textAlign: 'center', padding: '24px 0 16px' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#1890ff', marginBottom: 4 }}>
              {t('help.aboutModal.productName', { ns: 'common' })}
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 14 }}>
              {t('help.aboutModal.version', { ns: 'common' })}
            </Typography.Text>
          </div>
          <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label={<><BankOutlined style={{ marginRight: 6 }} />{t('help.aboutModal.companyLabel', { ns: 'common' })}</>}>
              {t('help.aboutModal.company', { ns: 'common' })}
            </Descriptions.Item>
            <Descriptions.Item label={<><MailOutlined style={{ marginRight: 6 }} />{t('help.aboutModal.contact', { ns: 'common' })}</>}>
              terry.sun@harbour-cn.com
            </Descriptions.Item>
            <Descriptions.Item label={<><EnvironmentOutlined style={{ marginRight: 6 }} />{t('help.aboutModal.address', { ns: 'common' })}</>}>
              {t('help.aboutModal.addressValue', { ns: 'common' })}
            </Descriptions.Item>
          </Descriptions>
          <div style={{ textAlign: 'center', color: '#999', fontSize: 12 }}>
            {t('help.aboutModal.copyright', { ns: 'common' })}
          </div>
        </Modal>

      </Layout>
    </Layout>
  )
}

// 根据路径获取页面标题（使用翻译键）
const getPageTitle = (pathname: string, t: (key: string, options?: any) => string): string => {
  const pathMap: Record<string, string> = {
    '/dashboard': 'navigation.pageTitles.dashboard',
    '/chatbot': 'navigation.pageTitles.chatbot',
    '/knowledge': 'navigation.pageTitles.knowledge',
    '/history': 'navigation.pageTitles.history',
    '/settings': 'navigation.pageTitles.settings',
    '/humans': 'navigation.pageTitles.users',
    '/companies': 'navigation.pageTitles.companies',
    '/profile': 'navigation.pageTitles.profile',
    '/notifications': 'navigation.pageTitles.notifications',
    '/help': 'navigation.pageTitles.help',
    '/agent-config': 'navigation.pageTitles.agentConfig'
  }

  // 精确匹配
  if (pathMap[pathname]) {
    return t(pathMap[pathname], { ns: 'modules' })
  }

  // 前缀匹配
  for (const [path, translationKey] of Object.entries(pathMap)) {
    if (pathname.startsWith(path + '/')) {
      return t(translationKey, { ns: 'modules' })
    }
  }

  return t('navigation.pageTitles.default', { ns: 'modules' })
}

// 导出一些常用的布局变体
export const DashboardLayout: React.FC<{ children: ReactNode }> = ({ children }) => (
  <AuthLayout showSidebar={true} showHeader={true}>
    {children}
  </AuthLayout>
)

export const SimpleLayout: React.FC<{ children: ReactNode }> = ({ children }) => (
  <AuthLayout showSidebar={false} showHeader={true}>
    {children}
  </AuthLayout>
)

export const MinimalLayout: React.FC<{ children: ReactNode }> = ({ children }) => (
  <AuthLayout showSidebar={false} showHeader={false} fullScreen={true}>
    {children}
  </AuthLayout>
)

export default AuthLayout